import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, ChevronRight } from 'lucide-react';
interface Section {
  id: string;
  title: string;
  description: string;
}
interface CaseStudyTableOfContentsProps {
  sections: Section[];
}
export function CaseStudyTableOfContents({
  sections
}: CaseStudyTableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>('');
  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scroll = `${totalScroll / windowHeight}`;
      setScrollProgress(Number(scroll));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Track active section
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -50% 0px'
    });
    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [sections]);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setIsOpen(false);
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <>
      {/* Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1.5 bg-slate-100 z-40" style={{
      scaleX: scrollProgress,
      transformOrigin: '0%'
    }} data-id="element-46">
        <div className="h-full w-full bg-gradient-to-r from-blue-400 to-blue-600" data-id="element-47" />
      </motion.div>

      {/* Floating Button */}
      <motion.button initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} whileHover={{
      scale: 1.1
    }} whileTap={{
      scale: 0.9
    }} onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full shadow-xl flex items-center justify-center hover:shadow-2xl hover:shadow-blue-500/30 transition-shadow" aria-label="Table of contents" data-id="element-48">
        <List size={24} strokeWidth={2.5} data-id="element-49" />
      </motion.button>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-50">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsOpen(false)} data-id="element-51">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-id="element-52">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-53">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-54">
                  Table of Contents
                </h2>
                <button onClick={() => setIsOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" aria-label="Close modal" data-id="element-55">
                  <X size={24} data-id="element-56" />
                </button>
              </div>

              {/* Section List */}
              <div className="overflow-y-auto p-4 md:p-6 space-y-2" data-id="element-57">
                {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`w-full text-left p-4 rounded-xl transition-all group flex items-start gap-4 ${activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-slate-50 border-l-4 border-transparent'}`} data-id="element-58">
                    <div className="flex-1" data-id="element-59">
                      <h3 className={`font-semibold mb-1 ${activeSection === section.id ? 'text-blue-700' : 'text-slate-900'}`} data-id="element-60">
                        {section.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-1" data-id="element-61">
                        {section.description}
                      </p>
                    </div>
                    <ChevronRight size={20} className={`mt-1 transition-transform ${activeSection === section.id ? 'text-blue-500 opacity-100' : 'text-slate-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-1'}`} data-id="element-62" />
                  </button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}